import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Tooltip,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Modal,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { AttachFile as AttachFileIcon, Clear as ClearIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddBranch = () => {
  const theme = useTheme();
  const [showMainBranchConfirmModal, setShowMainBranchConfirmModal] = useState(false);
const [mainBranchConfirmMessage, setMainBranchConfirmMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);

  // Credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [createdBranchName, setCreatedBranchName] = useState('');

  // Dropdown data
  const [organizations, setOrganizations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const navigate = useNavigate();
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes

  const [formData, setFormData] = useState({
    organization: '',
    name: '',
    country: '',
    state: '',
    city: '',
    address: '',
    email: '',
    mobile: '',
    landline: '',
    zipcode: '',
    tin_number: '',
    affiliation_number: '',
    fax: '',
    website: '',
    bank_name: '',
    bank_branch: '',
    account_holder: '',
    account_number: '',
    ifsc_code: '',
    gst_number: '',
    image: null,
    imagePreview: null,
    is_main_branch: false,
  });

   // Fetch organizations — restricted to the logged-in user's own organization
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/master/organizations/`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        const storedUser = JSON.parse(
          sessionStorage.getItem('user') ||
          localStorage.getItem('user') ||
          '{}'
        );

        const loggedOrganizationId =
          storedUser?.organization_id ??
          storedUser?.organization?.id ??
          storedUser?.organization ??
          '';

        const allOrganizations = response.data || [];

        const restrictedOrganizations = loggedOrganizationId
          ? allOrganizations.filter(
              (org) => String(org.id) === String(loggedOrganizationId)
            )
          : allOrganizations;

        setOrganizations(restrictedOrganizations);

        // Auto-select the user's organization, which also
        // populates country/state/city via the existing handler.
        if (restrictedOrganizations.length > 0) {
          handleOrganizationChange(
            String(restrictedOrganizations[0].id)
          );
        }
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
      }
    };
    fetchOrganizations();
  }, []);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        setCountries(response.data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  // Auto-fetch country, state, city when organization is selected
  const handleOrganizationChange = async (orgId) => {
    if (!orgId) {
      setFormData((prevData) => ({
        ...prevData,
        organization: '',
        country: '',
        state: '',
        city: '',
      }));
      return;
    }

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');

      // Fetch the selected organization details
      const response = await axios.get(`${API_BASE_URL}/api/master/organizations/${orgId}/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      const organizationData = response.data;

      // Auto-populate country, state, city from organization
      setFormData((prevData) => ({
        ...prevData,
        organization: orgId,
        country: organizationData.country || '',
        state: organizationData.state || '',
        city: organizationData.city || '',
      }));

      // Fetch states for the selected country
      if (organizationData.country) {
        const statesResponse = await axios.get(
          `${API_BASE_URL}/api/master/states/?country=${organizationData.country}`,
          { headers: { 'Authorization': `Bearer ${accessToken}` } }
        );
        const filteredStates = statesResponse.data.filter(
          (state) => state.country === parseInt(organizationData.country)
        );
        setStates(filteredStates);
      }

      // Fetch cities for the selected state
      if (organizationData.state) {
        const citiesResponse = await axios.get(
          `${API_BASE_URL}/api/master/cities/?state=${organizationData.state}`,
          { headers: { 'Authorization': `Bearer ${accessToken}` } }
        );
        const filteredCities = citiesResponse.data.filter(
          (city) => city.state === parseInt(organizationData.state)
        );
        setCities(filteredCities);
      }

    } catch (err) {
      console.error("Failed to fetch organization details:", err);
      setSnackbarMessage('Failed to fetch organization details');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (formData.country) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/states/?country=${formData.country}`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          const filteredStates = response.data.filter(
            (state) => state.country === parseInt(formData.country)
          );
          setStates(filteredStates);
        } catch (err) {
          console.error("Failed to fetch states:", err);
        }
      }
    };
    fetchStates();
  }, [formData.country]);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (formData.state) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/cities/?state=${formData.state}`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          const filteredCities = response.data.filter(
            (city) => city.state === parseInt(formData.state)
          );
          setCities(filteredCities);
        } catch (err) {
          console.error("Failed to fetch cities:", err);
        }
      }
    };
    fetchCities();
  }, [formData.state]);

  const handleChange = (e) => {
    const { name, value, files, checked, type } = e.target;

    if (name === 'image' && files && files[0]) {
      const file = files[0];

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setSnackbarMessage('Image size exceeds 1MB limit. Please choose a smaller image.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        e.target.value = '';
        return;
      }

      setFormData((prevData) => ({
        ...prevData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    } else if (name === 'organization') {
      handleOrganizationChange(value);
    } else if (type === 'checkbox') {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked,
      }));
    } else {
      // Auto-format website URL
      if (name === 'website' && value && !value.startsWith('http://') && !value.startsWith('https://')) {
        setFormData((prevData) => ({
          ...prevData,
          [name]: `https://${value}`,
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

const handleCopyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement("textarea");

      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";

      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");

      document.body.removeChild(textArea);

      if (!successful) {
        throw new Error("Copy command failed");
      }
    }

    setSnackbarMessage("Copied to clipboard!");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);

  } catch (error) {
    console.error("Failed to copy:", error);

    setSnackbarMessage("Failed to copy to clipboard");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  }
};
  const handleCredentialsModalClose = () => {
    setShowCredentialsModal(false);
    navigate(-1);
  };

  const submitSave = async (extraFields = {}) => {
  try {
    setLoading(true);
    const accessToken = localStorage.getItem('accessToken');

    const formDataToSend = new FormData();

    formDataToSend.append('organization', formData.organization);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('country', formData.country);
    formDataToSend.append('state', formData.state);
    formDataToSend.append('city', formData.city);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('mobile', formData.mobile);
    formDataToSend.append('zipcode', formData.zipcode);
    formDataToSend.append('is_main_branch', formData.is_main_branch);

    if (formData.landline) formDataToSend.append('landline', formData.landline);
    if (formData.tin_number) formDataToSend.append('tin_number', formData.tin_number);
    if (formData.affiliation_number) formDataToSend.append('affiliation_number', formData.affiliation_number);
    if (formData.fax) formDataToSend.append('fax', formData.fax);
    if (formData.website) formDataToSend.append('website', formData.website);
    if (formData.bank_name) formDataToSend.append('bank_name', formData.bank_name);
    if (formData.bank_branch) formDataToSend.append('bank_branch', formData.bank_branch);
    if (formData.account_holder) formDataToSend.append('account_holder', formData.account_holder);
    if (formData.account_number) formDataToSend.append('account_number', formData.account_number);
    if (formData.ifsc_code) formDataToSend.append('ifsc_code', formData.ifsc_code);
    if (formData.gst_number) formDataToSend.append('gst_number', formData.gst_number);

    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    Object.entries(extraFields).forEach(([key, val]) => {
      formDataToSend.append(key, val);
    });

    const response = await axios.post(
      `${API_BASE_URL}/api/master/branch/`,
      formDataToSend,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const responseData = response.data;

    if (responseData.confirm_main_branch_change) {
      const confirmMsg = Array.isArray(responseData.confirm_main_branch_change)
        ? responseData.confirm_main_branch_change[0]
        : responseData.confirm_main_branch_change;
      setMainBranchConfirmMessage(confirmMsg);
      setShowMainBranchConfirmModal(true);
      return;
    }

    if (responseData.credentials) {
      setCredentials(responseData.credentials);
      setCreatedBranchName(responseData.branch?.name || formData.name);
      setShowCredentialsModal(true);
    } else {
      setSnackbarMessage('Branch added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setTimeout(() => navigate(-1), 1000);
    }

  } catch (err) {
    if (err.response?.data?.confirm_main_branch_change) {
      const confirmMsg = Array.isArray(err.response.data.confirm_main_branch_change)
        ? err.response.data.confirm_main_branch_change[0]
        : err.response.data.confirm_main_branch_change;
      setMainBranchConfirmMessage(confirmMsg);
      setShowMainBranchConfirmModal(true);
      return;
    }

    let errorMessage = 'Failed to add branch';
    if (err.response?.data) {
      if (typeof err.response.data === 'object') {
        const errorObj = err.response.data;
        const firstKey = Object.keys(errorObj)[0];
        if (firstKey) {
          const val = errorObj[firstKey];
          errorMessage = Array.isArray(val) ? val.join(', ') : String(val);
        }
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    }
    setSnackbarMessage(errorMessage);
    setSnackbarSeverity('error');
    setOpenSnackbar(true);

    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      navigate('/login');
    }
  } finally {
    setLoading(false);
  }
};

const handleConfirmMainBranchChange = async () => {
  setShowMainBranchConfirmModal(false);
  await submitSave({ confirm_main_branch_change: true });
};

const handleCancelMainBranchChange = () => {
  setShowMainBranchConfirmModal(false);
  setFormData((prev) => ({ ...prev, is_main_branch: !prev.is_main_branch }));
};

const handleSave = async (e) => {
  e.preventDefault();

  if (
    !formData.organization ||
    !formData.name ||
    !formData.country ||
    !formData.state ||
    !formData.city ||
    !formData.address ||
    !formData.email ||
    !formData.mobile ||
    !formData.zipcode
  ) {
    setSnackbarMessage('Please fill all required fields');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  await submitSave();
};

  const handleCancel = () => navigate(-1);

  const handleReset = () => {
    setFormData({
      organization: '',
      name: '',
      country: '',
      state: '',
      city: '',
      address: '',
      email: '',
      mobile: '',
      landline: '',
      zipcode: '',
      tin_number: '',
      affiliation_number: '',
      fax: '',
      website: '',
      bank_name: '',
      bank_branch: '',
      account_holder: '',
      account_number: '',
      ifsc_code: '',
      gst_number: '',
      image: null,
      imagePreview: null,
      is_main_branch: false,
    });
    setStates([]);
    setCities([]);
  };

  const isFormValid = () => {
    return formData.organization && formData.name && formData.country &&
      formData.state && formData.city && formData.address &&
      formData.email && formData.mobile && formData.zipcode;
  };

  // TextField styling
  const textFieldStyle = {
    mb: 3,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.text.primary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
  };

  const selectFieldStyle = {
    mb: 3,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.text.primary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiSelect-select': {
      color: theme.palette.text.primary,
    },
  };

  const disabledFieldStyle = {
    mb: 3,
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.text.primary,
      },
      '&.Mui-disabled fieldset': {
        borderColor: theme.palette.grey[500],
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.text.primary,
      WebkitTextFillColor: theme.palette.text.primary,
    },
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Branch Details
          </Typography>
        </Box>

        <form onSubmit={handleSave}>
          <Grid container spacing={2}>
            {/* LEFT COLUMN */}
            <Grid item xs={12} md={6}>
              {/* Organization Dropdown */}
                            {/* Organization Dropdown — auto-selected & locked to logged-in user's org */}
              <FormControl fullWidth sx={disabledFieldStyle} required>
                <InputLabel shrink>Organization *</InputLabel>
                <Select
                  name="organization"
                  value={formData.organization}
                  label="Organization *"
                  disabled
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                          Loading organization...
                        </Typography>
                      );
                    }
                    const found = organizations.find((o) => String(o.id) === String(selected));
                    return found ? found.name : selected;
                  }}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Branch Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                sx={textFieldStyle}
              />

              {/* Country - Auto-filled and Disabled */}
              <FormControl fullWidth sx={disabledFieldStyle} required>
                <InputLabel shrink>Country *</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  label="Country *"
                  disabled={true}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                          Select organization first
                        </Typography>
                      );
                    }
                    const found = countries.find((c) => c.id === parseInt(selected));
                    return found ? found.name : selected;
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* State - Auto-filled and Disabled */}
              <FormControl fullWidth sx={disabledFieldStyle} required>
                <InputLabel shrink>State *</InputLabel>
                <Select
                  name="state"
                  value={formData.state}
                  label="State *"
                  disabled={true}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                          Select organization first
                        </Typography>
                      );
                    }
                    const found = states.find((s) => s.id === parseInt(selected));
                    return found ? found.name : selected;
                  }}
                >
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* City - Auto-filled and Disabled */}
              <FormControl fullWidth sx={disabledFieldStyle} required>
                <InputLabel shrink>City *</InputLabel>
                <Select
                  name="city"
                  value={formData.city}
                  label="City *"
                  disabled={true}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                          Select organization first
                        </Typography>
                      );
                    }
                    const found = cities.find((c) => c.id === parseInt(selected));
                    return found ? found.name : selected;
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Address *"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Email *"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Mobile No. *"
                name="mobile"
                inputProps={{ maxLength: 10 }}
                value={formData.mobile}
                onChange={handleChange}
                required
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Landline No."
                name="landline"
                inputProps={{ maxLength: 10 }}
                value={formData.landline}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Zip Code *"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                required
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="TIN Number"
                name="tin_number"
                value={formData.tin_number}
                onChange={handleChange}
                sx={textFieldStyle}
              />
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Affiliation Number"
                name="affiliation_number"
                value={formData.affiliation_number}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Fax"
                name="fax"
                value={formData.fax}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Bank Name"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Bank Branch"
                name="bank_branch"
                value={formData.bank_branch}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Account Holder Name"
                name="account_holder"
                value={formData.account_holder}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="Account Number"
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="IFSC Code"
                name="ifsc_code"
                value={formData.ifsc_code}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              <TextField
                fullWidth
                label="GST Number"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                sx={textFieldStyle}
              />

              {/* Is Main Branch Toggle */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: `1px solid ${formData.is_main_branch ? theme.palette.primary.main : theme.palette.text.primary}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'border-color 0.2s ease',
                }}
              >
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Set as Main Branch
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                    Designate this as the primary branch for the organization
                  </Typography>
                </Box>
                <Switch
                  name="is_main_branch"
                  checked={formData.is_main_branch}
                  onChange={handleChange}
                  color="primary"
                />
              </Box>

              <Box sx={{ mb: 1 }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  Choose Image
                  <input
                    type="file"
                    name="image"
                    onChange={handleChange}
                    accept="image/*"
                    hidden
                  />
                </Button>
              </Box>

              <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 500, ml: 1, display: 'block', mb: 2 }}>
                Maximum image size: 1MB
              </Typography>

              {formData.imagePreview && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                  />
                  <IconButton onClick={handleRemoveImage} color="error" size="small">
                    <ClearIcon />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid() || loading}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              {loading ? 'SAVING...' : 'SAVE'}
            </Button>
            <Button
              variant="contained"
              onClick={handleReset}
              disabled={loading}
            >
              RESET
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={loading}
            >
              BACK
            </Button>
          </Box>
        </form>
      </Box>

      {/* Credentials Modal */}
      <Modal
  open={showCredentialsModal}
  onClose={handleCredentialsModalClose}
  aria-labelledby="credentials-modal-title"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 480,
      bgcolor: theme.palette.background.paper,
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
      <Typography id="credentials-modal-title" variant="h6" sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}>
        🎉 Branch Created Successfully!
      </Typography>
    </Box>
    <Box sx={{ padding: '24px' }}>
      <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.primary }}>
        Branch <strong>{createdBranchName}</strong> has been created. Please save and share the following login credentials with the branch super admin.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Login Credentials
        </Typography>
        <Tooltip title="Copy All">
          <Button
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={() =>
              handleCopyToClipboard(
                `Username: ${credentials.username}\nPassword: ${credentials.password}`
              )
            }
            sx={{
              fontSize: '0.75rem',
              textTransform: 'none',
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}`,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            Copy All
          </Button>
        </Tooltip>
      </Box>

      {/* Username Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          mb: 1.5,
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block' }}>
            Username
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600, fontFamily: 'monospace' }}>
            {credentials.username}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => handleCopyToClipboard(credentials.username)}
          sx={{ color: theme.palette.primary.main }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Password Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          mb: 2,
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block' }}>
            Password
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600, fontFamily: 'monospace' }}>
            {credentials.password}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => handleCopyToClipboard(credentials.password)}
          sx={{ color: theme.palette.primary.main }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="caption" sx={{ color: theme.palette.warning.main, display: 'block', mb: 3 }}>
        ⚠️ These credentials are shown only once. Please save them before closing.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* WhatsApp Share Button */}
        <Button
          variant="outlined"
          startIcon={
            <Box
              component="img"
              src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
              alt="WhatsApp"
              sx={{ width: 20, height: 20 }}
            />
          }
          onClick={() => {
            const message = encodeURIComponent(
              `🎉 *Branch Login Credentials*\n\n` +
              `*Branch:* ${createdBranchName}\n` +
              `*Username:* ${credentials.username}\n` +
              `*Password:* ${credentials.password}\n\n` +
              `⚠️ Please keep these credentials safe.`
            );
            window.open(`https://wa.me/?text=${message}`, '_blank');
          }}
          sx={{
            textTransform: 'none',
            color: '#25D366',
            borderColor: '#25D366',
            '&:hover': {
              borderColor: '#1ebe57',
              backgroundColor: 'rgba(37, 211, 102, 0.05)',
            },
          }}
        >
          Share via WhatsApp
        </Button>

        <Button
          variant="contained"
          onClick={handleCredentialsModalClose}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          Done
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Main Branch Change Confirmation Modal */}
<Modal
  open={showMainBranchConfirmModal}
  onClose={handleCancelMainBranchChange}
  aria-labelledby="main-branch-confirm-title"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 460,
      bgcolor: theme.palette.background.paper,
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
      <Typography
        id="main-branch-confirm-title"
        variant="h6"
        sx={{ color: theme.palette.primary.contrastText, fontWeight: 'bold' }}
      >
        Main Branch Change ⚠️
      </Typography>
    </Box>
    <Box sx={{ padding: '24px' }}>
      <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.primary }}>
        {mainBranchConfirmMessage}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: theme.palette.warning.main }}>
        This action will transfer the main branch status. Please confirm to proceed.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleCancelMainBranchChange}
          disabled={loading}
         data-app-cancel="true">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmMainBranchChange}
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {loading ? 'Confirming...' : 'Yes, Confirm'}
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>
    </Container>
  );
};

export default AddBranch;